/**
 * GoalContentTableV2 — Topics list (V2 redesign).
 *
 * Every topic (including the `general` fallback and retired ones), in their
 * CMS-controlled display order. Drag any row to reorder — the new order is
 * persisted on each drop (PUT /admin/goal-content/sort-order) and is the chip
 * order in both the onboarding screen and the Settings topic picker. Columns:
 * drag handle, #, Topic (with Fallback / Retired badges), Welcome Title, Offer
 * Title, Onboarding, Settings, Onboarding Session ID, Actions (Recommended Sessions, Edit).
 *
 * Data source: `state.goalContent.goalContents` (already ordered by
 * display_order from the API) via `fetchAllGoalContent`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { fetchAllGoalContent, reorderGoalContent } from '../../../actions/goalContentActions';
import { goalLabel } from '../../../constants/goalLabels';
import { LibTable, LibSection, IconButton, StatusPill, cn } from '../../redesign';

/* ─── Icons ─────────────────────────────────────────────────────────── */

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

// The onboarding recommendation is polymorphic (a Session or a Quick Tap). The
// legacy `recommended_session_id` is null for a Quick Tap, so read the
// type/id pair and fall back to the legacy id for rows that predate it.
export function onboardingContentLabel(content) {
  const type = content.recommended_content_type;
  const id = content.recommended_content_id;
  if (type === 'quick-tap-session' && id != null) return `QT ${id}`;
  if (type === 'session' && id != null) return String(id);
  return content.recommended_session_id != null ? String(content.recommended_session_id) : '—';
}

const YesNo = ({ value }) => (
  <span className={value ? 'text-navy' : 'text-text-muted'} style={{ fontSize: 12, fontWeight: value ? 600 : 400 }}>
    {value ? 'Yes' : 'No'}
  </span>
);

/* ─── Sortable row ─────────────────────────────────────────────────── */

function SortableGoalRow({ content, position }) {
  const slug = content.goal_slug;
  // Prefer the DB-backed label; fall back to the legacy constant / slug so the
  // table still reads sensibly if a row's label is ever blank.
  const label = content.label || goalLabel(slug);
  // `general` is the onboarding fallback — never a pickable topic, so it has no session list.
  const isFallback = slug === 'general';

  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slug });

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row ref={setNodeRef} style={dragStyle} {...attributes}>
      {/* Drag handle — `listeners` only here so the rest of the row stays clickable. */}
      <td className="border-b border-line text-center" style={{ width: 36, padding: '10px 0 10px 10px' }}>
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'inline-grid place-items-center text-text-muted cursor-grab',
            'opacity-[0.35] group-hover:opacity-100',
            'hover:bg-cream hover:text-navy active:cursor-grabbing',
            'focus:outline-none focus-visible:bg-cream focus-visible:text-navy',
          )}
          style={{ width: 26, height: 26, borderRadius: 4, transition: 'all 140ms ease', touchAction: 'none' }}
        >
          <span style={{ width: 13, height: 13, display: 'inline-flex' }}>{DragHandleIcon}</span>
        </span>
      </td>

      <LibTable.NumTd muted>{position}</LibTable.NumTd>

      <LibTable.Td>
        <Link
          to={`/goal-content/edit/${slug}`}
          className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
          style={{ fontSize: 14, lineHeight: 1.25 }}
          title={label}
        >
          {label}
        </Link>
        <div className="flex items-center" style={{ gap: 6, marginTop: 2, minWidth: 0 }}>
          <span className="font-mono text-text-muted truncate" style={{ fontSize: 11 }}>{slug}</span>
          {isFallback && <StatusPill tone="blue">Fallback</StatusPill>}
          {content.is_retired && <StatusPill tone="gray">Retired</StatusPill>}
        </div>
      </LibTable.Td>

      <LibTable.Td>
        <span className="text-text-muted" style={{ fontSize: 12 }}>{content.welcome_title || '—'}</span>
      </LibTable.Td>

      <LibTable.Td>
        <span className="text-text-muted" style={{ fontSize: 12 }}>{content.offer_title || '—'}</span>
      </LibTable.Td>

      <LibTable.CenterTd><YesNo value={!!content.show_in_onboarding} /></LibTable.CenterTd>
      <LibTable.CenterTd><YesNo value={!!content.show_in_settings} /></LibTable.CenterTd>

      <LibTable.CenterTd>
        <span className="text-text-muted" style={{ fontSize: 12 }}>
          {onboardingContentLabel(content)}
        </span>
      </LibTable.CenterTd>

      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex items-center" style={{ gap: 6 }}>
          {!isFallback && (
            <Link
              to={`/goal-content/${slug}/sessions`}
              aria-label={`Recommended Sessions for ${label}`}
              className="font-semibold text-navy no-underline hover:text-teal-green-dark"
              style={{ fontSize: 12 }}
            >
              Recommended Sessions
            </Link>
          )}
          <span className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
            <Link to={`/goal-content/edit/${slug}`} aria-label={`Edit ${label}`}>
              <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
            </Link>
          </span>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function GoalContentTableV2() {
  const dispatch = useDispatch();
  const goalContents = useSelector((s) => s.goalContent?.goalContents);

  // Local ordered list — the draggable source of truth. Re-syncs whenever the
  // server list changes (initial load, or a reorder failure that re-fetches).
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    dispatch(fetchAllGoalContent());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(goalContents)) setLocalList(goalContents);
  }, [goalContents]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localList.findIndex((c) => c.goal_slug === active.id);
      const newIndex = localList.findIndex((c) => c.goal_slug === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);
      dispatch(reorderGoalContent(next.map((c) => c.goal_slug)));
    },
    [localList, dispatch],
  );

  const ids = useMemo(() => localList.map((c) => c.goal_slug), [localList]);

  if (!Array.isArray(goalContents)) {
    return (
      <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
        Loading topics…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      <LibSection
        first
        title="Topics"
        count={localList.length}
        hint="Drag any row to reorder · saved on each drop · this is the chip order in onboarding and the Settings topic picker"
      >
        {localList.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            No topics found.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <LibTable fixed className="border border-sand" style={{ borderRadius: 12, overflow: 'hidden' }}>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 48 }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: 92 }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 200 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th aria-label="Drag to reorder"> </LibTable.Th>
                  <LibTable.NumTh>#</LibTable.NumTh>
                  <LibTable.Th>Goal</LibTable.Th>
                  <LibTable.Th>Welcome Title</LibTable.Th>
                  <LibTable.Th>Offer Title</LibTable.Th>
                  <LibTable.CenterTh>Onboarding</LibTable.CenterTh>
                  <LibTable.CenterTh>Settings</LibTable.CenterTh>
                  <LibTable.CenterTh>Onboarding Session ID</LibTable.CenterTh>
                  <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
                </tr>
              </LibTable.Head>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <tbody>
                  {localList.map((content, idx) => (
                    <SortableGoalRow key={content.goal_slug} content={content} position={idx + 1} />
                  ))}
                </tbody>
              </SortableContext>
            </LibTable>
          </DndContext>
        )}
      </LibSection>
    </div>
  );
}
